import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { IconButton, Status } from '../ui';

const PAGE_SIZE = 12;

/**
 * GalleryMobileList — mobile-only infinite scroll list.
 *
 * Renders posts in a 2-column grid, one batch at a time.
 * An IntersectionObserver sentinel at the bottom of the list
 * triggers loading the next batch automatically as the user scrolls.
 *
 * Props:
 *   posts:       Post[]          — already-filtered post array
 *   onPostClick: (post) => void
 *   onDownload:  (url, filename) => void
 */
const GalleryMobileList = ({ posts, onPostClick, onDownload }) => {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef(null);

  // Reset pagination whenever the filtered list changes
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [posts]);

  const loadMore = useCallback(() => {
    setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, posts.length));
  }, [posts.length]);

  // Attach IntersectionObserver to the sentinel div
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore]);

  const visiblePosts = posts.slice(0, visibleCount);
  const hasMore = visibleCount < posts.length;

  return (
    <div className='hidden'>
      <div className='flex flex-col gap-3'>
        {visiblePosts.map((post) => {
          const isDraft = post.status === 'draft';
          const src = post.path || post.imageUrl;
          const isStory = post.aspectRatio === '9:16';

          return (
            <div
              key={post.id}
              className='relative rounded-xl overflow-hidden'
              style={{
                border:     '1px solid var(--border)',
                background: 'var(--bg-card)',
                cursor:     isDraft ? 'not-allowed' : 'pointer',
              }}
              onClick={() => !isDraft && onPostClick(post)}
            >
              {isDraft ? (
                <div
                  className={`flex flex-col items-center justify-center gap-2 p-4 ${isStory ? 'aspect-[9/16]' : 'aspect-square'}`}
                  style={{ background: 'var(--bg-surface)' }}
                >
                  <Loader2 className='w-6 h-6 animate-spin' style={{ color: 'var(--brand)' }} />
                  <span className='text-[10px] text-center' style={{ color: 'var(--text-muted)' }}>
                    Generating...
                  </span>
                </div>
              ) : (
                <>
                  <img
                    src={src}
                    alt='Post content'
                    className='w-full block'
                    loading='lazy'
                  />

                  {/* Persistent bottom bar on mobile (no hover) */}
                  <div
                    className='absolute bottom-0 inset-x-0 flex items-center justify-between px-2 py-1.5'
                    style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 100%)' }}
                  >
                    <Status status={post.status} />
                    <IconButton
                      icon={Download}
                      onClick={(e) => {
                        e.stopPropagation();
                        onDownload(src, `post-${post.id}`);
                      }}
                      variant='ghostPrimary'
                      tooltip='Download'
                      size='sm'
                      tooltipPosition='top'
                    />
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Sentinel — triggers load-more when scrolled into view */}
      {hasMore && (
        <div ref={sentinelRef} className='flex justify-center py-6'>
          <Loader2 className='w-5 h-5 animate-spin' style={{ color: 'var(--text-muted)' }} />
        </div>
      )}

      {!hasMore && posts.length > PAGE_SIZE && (
        <p className='text-center py-6 text-xs' style={{ color: 'var(--text-muted)' }}>
          All {posts.length} posts loaded
        </p>
      )}
    </div>
  );
};

export default GalleryMobileList;
